"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IconMail } from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";

interface IForgotPasswordResponseData {
  "message"?: string;
  "error"?: string;
}

/**
 * Sets layout and content for password retrieval page.
 *
 * @returns All elements of the page.
 */
export default function ForgotPassword(): React.ReactElement {
  const router = useRouter();
  const [requestError, setRequestError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const email = useRef<HTMLInputElement>(null);

  return (
    <div className="container p-4 mx-auto max-w-[500px]">
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Forgot Your Password?
          </CardTitle>
          <CardDescription className="text-center">
            Enter your email address and we will send you a six-digit code to
            reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(event): void => {
              event.preventDefault();
              setRequestError(undefined);
              setIsLoading(true);

              fetch("/api/auth/forgot-password", {
                "body": JSON.stringify({
                  "email": email.current?.value,
                }),
                "method": "POST",
              })
                .then(async (res) => res.json())
                .then((data: IForgotPasswordResponseData) => {
                  if (typeof data.error !== "undefined") {
                    setRequestError(data.error);
                    console.error(data.error);
                  } else {
                    router.push(
                      "/log-in/forgot-password/reset-password?" +
                        `email=${encodeURIComponent(
                          email.current?.value ?? ""
                        )}`
                    );
                  }
                })
                .catch((error) => {
                  console.error(error);
                })
                .finally(() => {
                  setIsLoading(false);
                });
            }}
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="forgot-password-email"
                  className="text-sm font-medium"
                >
                  Email
                </Label>
                <div className="relative">
                  <IconMail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    className="pl-10"
                    id="forgot-password-email"
                    placeholder="your@email.com"
                    autoComplete="email"
                    ref={email}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {requestError !== undefined && requestError !== "" && (
                <Alert variant="destructive">
                  <AlertDescription>{requestError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Sending..." : "Send verification code"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
